import buildUrl from 'build-url';
import useOneHeaderContext from '../hooks/useOneHeaderContext';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import useConfig from 'Client/hooks/useConfig';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { ensureEndingSlash } from 'Client/urlService';
import { TrackHeaderActions } from 'Common/ga/types';
import useGa from 'Client/hooks/useGa';

interface Config {
    isClubTabHighlighted: boolean;
    fromBurgerMenu: boolean;
    isLoggedIn: boolean;
}

export const getClubUrlWithUTM = (
    baseUrl: string,
    { isClubTabHighlighted = false, fromBurgerMenu = false, isLoggedIn }: Config
) => {
    const pr = 'fetch';

    const queryParams = {
        utm_source: 'shop',
        utm_medium: 'referral',
        utm_campaign: isClubTabHighlighted ? 'highlightedclubtab_prfetch' : 'shop-club-tab',
        utm_content: fromBurgerMenu ? 'burger-menu' : 'header',
        ...(isClubTabHighlighted && { pr }),
    };

    // For logged in users we need to send all query params url encoded as one string appended to /campaign/ within the redirect_to param.
    // The implementation to decoded this and redirect to the correct campaign is in the seo endpoints in Alice.
    const queryParamsLoggedIn = {
        redirect_to: `/campaign/?utm_source=${queryParams.utm_source}&utm_medium=${queryParams.utm_medium}&utm_campaign=${queryParams.utm_campaign}&utm_contend=${queryParams.utm_content}`,
    };

    return buildUrl(ensureEndingSlash(baseUrl), {
        queryParams: isLoggedIn ? queryParamsLoggedIn : queryParams,
    });
};

const useClubTab = (fromBurgerMenu: boolean) => {
    const t = useBellaTranslator();
    const config = useConfig();
    const ga = useGa();

    const isEnabled = useOneHeaderContext().clubHeaderHighlightTab?.toggle;

    const {
        links: { clubBaseUrl, singleSignOn: singleSignOnUrl },
    } = config;

    let clubText = t('Westwing tab subline - Header redesign');
    const { isRegistered, didLoginFinishOnce, isLoggedIn } = useUserTracking();
    const { clubLinkTabHighlight } = useFeatureToggle();

    const isClubTabHighlighted = !!didLoginFinishOnce && !isRegistered && !!isEnabled && !!clubLinkTabHighlight;

    let clubUrl = clubBaseUrl;

    if (isClubTabHighlighted) {
        clubText = t('Club tab_new Club sales_desktop');
    }

    if (isLoggedIn) {
        clubUrl = singleSignOnUrl;
    }

    const clubUrlWithUTM = getClubUrlWithUTM(clubUrl, {
        isClubTabHighlighted,
        fromBurgerMenu,
        isLoggedIn: !!isLoggedIn,
    });

    const clickTrackingFn = fromBurgerMenu
        ? () => ga.oneHeader.trackHeaderAction('clubtabclick', TrackHeaderActions.HeaderClubLinkClickFromMenu)
        : () => ga.oneHeader.trackHeaderAction('clubtabclick', TrackHeaderActions.HeaderClubLinkClick);

    return { clubText, clubUrl: clubUrlWithUTM, isClubTabHighlighted, clickTrackingFn };
};

export default useClubTab;
